import type { Brush } from '../../../../shared/src/collision/brushes.js';

/**
 * Fixed-north minimap, top-left corner.
 *
 * Two layers, deliberately split by change rate:
 * - the WALLS render once into an offscreen canvas at construction — map geometry never
 *   changes during a match (the join handshake hash-checks it), so repainting brushes per
 *   frame would be 60Hz of work for a picture that cannot change;
 * - the MARKERS (self, allies, enemy pings, grenade bursts) repaint every frame over a
 *   `drawImage` of that static layer.
 *
 * What it shows is deliberately less than what the client knows. The snapshot stream carries
 * every enemy's position at all times — drawing them would be a wallhack the server cannot
 * prevent, since the data is already here for rendering the world. So enemies appear only as
 * short-lived pings when they FIRE (the shot event is public information — everyone heard the
 * gun), and grenade bursts ping the same way. Allies draw continuously: the server tells
 * teammates apart by slot parity and nothing about an ally's position is secret.
 *
 * Fixed north rather than rotate-with-player: a rotating map is easier to follow in the
 * moment but destroys the one thing a minimap is FOR in a round-based shooter — building a
 * stable mental picture of the map. The self marker rotates instead.
 */

export interface MinimapBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** Canvas edge, CSS pixels. */
export const MINIMAP_SIZE_PX = 180;
/** How long an enemy stays pinged after firing. */
export const ENEMY_PING_TTL_MS = 2000;
/** How long a grenade burst pings. */
export const BURST_PING_TTL_MS = 1000;

/**
 * A brush is drawn when something about it rises above knee height — walls, crates, cover.
 * Floor slabs and steps span the whole playfield and would paint the map solid; the 1m line
 * keeps them out while keeping everything a player can actually collide with laterally.
 */
export function isMinimapWall(brush: Brush): boolean {
  return brush.center.y + brush.halfExtents.y > 1.0;
}

/** Bounds over ALL brushes (the floor is what actually spans the playable area), plus margin. */
export function minimapBounds(brushes: readonly Brush[], marginM = 2): MinimapBounds {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const brush of brushes) {
    minX = Math.min(minX, brush.center.x - brush.halfExtents.x);
    maxX = Math.max(maxX, brush.center.x + brush.halfExtents.x);
    minZ = Math.min(minZ, brush.center.z - brush.halfExtents.z);
    maxZ = Math.max(maxZ, brush.center.z + brush.halfExtents.z);
  }
  if (!Number.isFinite(minX)) return { minX: -1, maxX: 1, minZ: -1, maxZ: 1 };
  return { minX: minX - marginM, maxX: maxX + marginM, minZ: minZ - marginM, maxZ: maxZ + marginM };
}

/**
 * World XZ → canvas pixels. North (-Z, the yaw-0 facing) is up, so `py` grows with +Z. The
 * scale letterboxes the longer world axis to fit — one uniform scale, never stretched, so
 * distances read the same in every direction.
 */
export function worldToMinimap(
  bounds: MinimapBounds,
  sizePx: number,
  x: number,
  z: number,
): { px: number; py: number } {
  const spanX = bounds.maxX - bounds.minX;
  const spanZ = bounds.maxZ - bounds.minZ;
  const scale = sizePx / Math.max(spanX, spanZ);
  const offsetX = (sizePx - spanX * scale) / 2;
  const offsetY = (sizePx - spanZ * scale) / 2;
  return {
    px: offsetX + (x - bounds.minX) * scale,
    py: offsetY + (z - bounds.minZ) * scale,
  };
}

/** A bombsite as the minimap draws it: the box, and its letter. */
export interface MinimapSite {
  id: string;
  center: { x: number; z: number };
  halfExtents: { x: number; z: number };
}

export interface MinimapMarkers {
  /** The local player, or null while dead (a corpse's position is not where the camera is). */
  self: { x: number; z: number; yaw: number } | null;
  allies: ReadonlyArray<{ x: number; z: number }>;
  enemyPings: ReadonlyArray<{ x: number; z: number; ageMs: number }>;
  burstPings: ReadonlyArray<{ x: number; z: number; ageMs: number }>;
  /**
   * Bombsites, drawn under everything else. Empty in every mode without an objective.
   *
   * Passed per frame rather than baked into the static layer, because the static layer is
   * built from the map's collision brushes at construction and a site is not one — and
   * because a planted bomb needs to be drawn on top of its own site.
   */
  sites?: ReadonlyArray<MinimapSite>;
  /** Where the bomb is, when it is somewhere worth drawing (dropped or planted). */
  bomb?: { x: number; z: number; planted: boolean } | null;
}

export class Minimap {
  private readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly staticLayer: HTMLCanvasElement;
  private readonly bounds: MinimapBounds;
  private readonly dpr: number;

  constructor(container: HTMLElement, brushes: readonly Brush[]) {
    this.bounds = minimapBounds(brushes);
    // `window.devicePixelRatio`, guarded: jsdom has no meaningful value and the class must
    // stay constructible in tests even though drawing is a no-op there (no 2d context).
    this.dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    this.root = document.createElement('div');
    this.root.dataset.arena = 'minimap-canvas';
    // No chrome of its own: the player HUD's radar frame (`PlayerHud.minimapHost`) is the
    // container, and it carries the panel, the map's name and the north mark. This only
    // paints the picture.
    this.root.className = 'pointer-events-none select-none';

    this.canvas = document.createElement('canvas');
    this.canvas.width = MINIMAP_SIZE_PX * this.dpr;
    this.canvas.height = MINIMAP_SIZE_PX * this.dpr;
    this.canvas.style.width = `${MINIMAP_SIZE_PX}px`;
    this.canvas.style.height = `${MINIMAP_SIZE_PX}px`;
    this.root.appendChild(this.canvas);
    container.appendChild(this.root);

    this.staticLayer = document.createElement('canvas');
    this.staticLayer.width = this.canvas.width;
    this.staticLayer.height = this.canvas.height;
    this.ctx = this.canvas.getContext('2d');
    this.paintStaticLayer(brushes);
  }

  /** Ground + wall footprints, painted once. */
  private paintStaticLayer(brushes: readonly Brush[]): void {
    const ctx = this.staticLayer.getContext('2d');
    if (!ctx) return;
    ctx.scale(this.dpr, this.dpr);
    ctx.clearRect(0, 0, MINIMAP_SIZE_PX, MINIMAP_SIZE_PX);
    ctx.fillStyle = 'rgba(15, 23, 42, 0.35)';
    ctx.fillRect(0, 0, MINIMAP_SIZE_PX, MINIMAP_SIZE_PX);
    ctx.fillStyle = 'rgba(226, 232, 240, 0.5)';
    for (const brush of brushes) {
      if (!isMinimapWall(brush)) continue;
      const a = worldToMinimap(this.bounds, MINIMAP_SIZE_PX, brush.center.x - brush.halfExtents.x, brush.center.z - brush.halfExtents.z);
      const b = worldToMinimap(this.bounds, MINIMAP_SIZE_PX, brush.center.x + brush.halfExtents.x, brush.center.z + brush.halfExtents.z);
      // At this scale a thin wall can round to zero pixels; a 1px floor keeps it visible.
      ctx.fillRect(a.px, a.py, Math.max(1, b.px - a.px), Math.max(1, b.py - a.py));
    }
  }

  update(markers: MinimapMarkers): void {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.staticLayer, 0, 0);
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    // Sites first, under everything: they are geography, not events.
    for (const site of markers.sites ?? []) {
      const min = worldToMinimap(
        this.bounds,
        MINIMAP_SIZE_PX,
        site.center.x - site.halfExtents.x,
        site.center.z - site.halfExtents.z,
      );
      const max = worldToMinimap(
        this.bounds,
        MINIMAP_SIZE_PX,
        site.center.x + site.halfExtents.x,
        site.center.z + site.halfExtents.z,
      );
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.75)';
      ctx.lineWidth = 1;
      ctx.strokeRect(min.px, min.py, max.px - min.px, max.py - min.py);
      ctx.fillStyle = 'rgba(251, 191, 36, 0.9)';
      ctx.font = 'bold 9px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(site.id, (min.px + max.px) / 2, (min.py + max.py) / 2);
    }

    if (markers.bomb) {
      const { px, py } = worldToMinimap(this.bounds, MINIMAP_SIZE_PX, markers.bomb.x, markers.bomb.z);
      // A planted bomb is the round: it is drawn bright and larger than a dropped one, which
      // is only an errand.
      ctx.fillStyle = markers.bomb.planted ? 'rgb(248, 113, 113)' : 'rgba(251, 191, 36, 0.9)';
      ctx.beginPath();
      ctx.arc(px, py, markers.bomb.planted ? 3.5 : 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Bursts under pings under allies under self — the thing the player controls draws last.
    for (const burst of markers.burstPings) {
      const { px, py } = worldToMinimap(this.bounds, MINIMAP_SIZE_PX, burst.x, burst.z);
      const fade = Math.max(0, 1 - burst.ageMs / BURST_PING_TTL_MS);
      ctx.fillStyle = `rgba(251, 146, 60, ${(0.9 * fade).toFixed(2)})`;
      ctx.beginPath();
      // The ring grows as it fades — a blast, not a person.
      ctx.arc(px, py, 3 + (1 - fade) * 5, 0, Math.PI * 2);
      ctx.fill();
    }

    for (const ping of markers.enemyPings) {
      const { px, py } = worldToMinimap(this.bounds, MINIMAP_SIZE_PX, ping.x, ping.z);
      const fade = Math.max(0, 1 - ping.ageMs / ENEMY_PING_TTL_MS);
      ctx.fillStyle = `rgba(239, 68, 68, ${(0.95 * fade).toFixed(2)})`;
      ctx.beginPath();
      ctx.arc(px, py, 3.2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = 'rgba(34, 211, 238, 0.9)';
    for (const ally of markers.allies) {
      const { px, py } = worldToMinimap(this.bounds, MINIMAP_SIZE_PX, ally.x, ally.z);
      ctx.beginPath();
      ctx.arc(px, py, 2.6, 0, Math.PI * 2);
      ctx.fill();
    }

    if (markers.self) {
      const { px, py } = worldToMinimap(this.bounds, MINIMAP_SIZE_PX, markers.self.x, markers.self.z);
      ctx.save();
      ctx.translate(px, py);
      // Forward at yaw 0 is -Z = screen-up; the canvas rotation that maps up onto the facing
      // is -yaw (same convention as `damageIndicatorAngle`, opposite sign because the canvas
      // rotates the MARKER rather than the world).
      ctx.rotate(-markers.self.yaw);
      ctx.fillStyle = '#7dfaf0';
      ctx.beginPath();
      ctx.moveTo(0, -5);
      ctx.lineTo(3.6, 4);
      ctx.lineTo(0, 2);
      ctx.lineTo(-3.6, 4);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }

  dispose(): void {
    this.root.remove();
  }
}
